import { Component, OnDestroy, OnInit, ElementRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  Country,
  CountryService,
  ListOfUser,
  User,
  UserService,
  UserUpdateCountriesInner
} from "../../../../swaggerServices";
import { UserInfoService} from "../../user-info.service";
import { cloneDeep } from "lodash-es";
import { asMap } from "../../../shared/util/asMap";
import { UserProvisioningService } from "../../services/user/user-provisioning.service";
import { forkJoin, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ManageUserComponent } from './manage-user/manage-user.component';

declare var $: any;

interface IUser extends User {
  BU?: string;
  countriesKeyword?: string
}

interface ManageUserDialogData {
  id: number;
  email: string;
  lastName: string;
  firstName: string;
}

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss'],
    standalone: false
})
export class UsersComponent implements OnInit, OnDestroy {
  @ViewChild('addUserModal') addUserModal: ElementRef;
  @ViewChild('confirmDeleteModal') confirmDeleteModal: ElementRef;

  users: IUser[];
  originalUsers: User[];
  countriesMap: Map<string, Country> = new Map();
  loading = true;
  isExporting: boolean;

  userSearch: string;
  isOnlyOneSuperAdmin: boolean;
  isSaving: boolean;
  showUserModalType: string;

  private destroy$ = new Subject<void>();

  constructor(
    private userService: UserService,
    private countryService: CountryService,
    public userInfo: UserInfoService,
    private userProvisionService: UserProvisioningService,
    private dialog: MatDialog,
  ) { }

  ngOnInit() {
    this.getUsers();
    this.countryService.getCountrys().subscribe((countries: any) => {
      this.countriesMap = asMap(countries, cnt => cnt.countryCode);
    });

    this.userProvisionService.updateObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getUsers();
      });
  }

  ngOnDestroy() {
    // Facilitate unsubscribing from active subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }

  getUsers() {
    this.userService.getUsers().subscribe((users: any) => {
      this.initUsers(users);
      this.loading = false;
    });
  }

  initUsers(users: ListOfUser) {
    this.users = users;
    this.checkIsOnlyOneSuperAdmin();
    this.users.map(u => {
      this.userInfo.groups.map(cnt => u[cnt] = false);
      u.countries.map(cnt => u[cnt.countryCode] = true);
    });
    this.originalUsers = cloneDeep(this.users);
    this.users.map(u => this.changeGroup(u));
  }

  changeRole(user) {
    this.enableAllCountriesIfSuperAdmin(user);
    this.checkIsOnlyOneSuperAdmin();
  }

  changeGroup(user) {
    user.countriesKeyword = this.userInfo.groups.filter(group => user[group]).toString();
  }

  enableAllCountriesIfSuperAdmin(user) {
    if (this.isSuperAdmin(user)) {
      this.userInfo.groups.map(group => user[group] = true);
    }
  }

  checkIsOnlyOneSuperAdmin() {
    this.isOnlyOneSuperAdmin = this.users.filter(u => this.isSuperAdmin(u)).length === 1;
  }

  canEditRole(u) {
    if (this.isLoggedIn(u)) return false;
    if (this.isOnlyOneSuperAdmin && this.isSuperAdmin(u)) return false;
    if (this.userInfo.isAdmin && !this.userInfo.isSuperAdmin) {
      if (this.isSuperAdmin(u)) return false;
    }
    return u.countries.every((cnt) => !!this.userInfo.countries.find(ccnt => ccnt.id === cnt.id));
  }

  canEditGroup(u) {
    return !this.isSuperAdmin(u) && this.canEditRole(u);
  }

  isLoggedIn(u) {
    return u && this.userInfo.id === u.id;
  }

  isSuperAdmin(u) {
    return u.role && u.role === 'SUPER_ADMIN';
  }

  isAdmin(u) {
    return u.role && u.role === 'COUNTRY_ADMIN';
  }

  save() {
    this.isSaving = true;
    const saveObs = [];
    this.users.forEach(u => {
      const originalU = this.originalUsers.find(_u => _u.id === u.id);
      if (originalU) {
        const roleChanged = u["role"] !== originalU["role"];
        const groupsToCompare = this.userInfo.groups;
        const groupsChanged: UserUpdateCountriesInner[] = [];
        groupsToCompare.forEach((cntCode: any) => {
          if (roleChanged || u[cntCode] !== originalU[cntCode]) {
            groupsChanged.push({
              country: this.countriesMap.get(cntCode),
              access: u[cntCode],
            });
          };
        });
        if (roleChanged || groupsChanged.length > 0) {
          saveObs.push(this.userService.updateUser({ id: u.id, role: u.role, countries: groupsChanged }));
        }
      }
    });
    if (!!saveObs.length) {
      this.saveUsers(saveObs);
    } else {
      this.isSaving = false;
    }
  }

  saveUsers(saveObs: any) {
    forkJoin(saveObs).subscribe(() => {
      this.getUsers();
      this.isSaving = false;
    },
    () => {
      this.getUsers();
      this.isSaving = false
    })
  }

  exportUserPermissions() {
    this.isExporting = true;
    const usersToExport = this.users || [];

    if (!usersToExport.length) {
      this.isExporting = false;
      return;
    }

    const isSuperAdminRequest = !!this.userInfo.isSuperAdmin;
    const headers: { [key: string]: string } = {
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email",
      BU: "Business Unit",
    };

    if (isSuperAdminRequest) {
      headers.role = "Role of User";
      headers.lastLoginDate = "Last Login Date";
    }

    // convert to csv
    let csv = Object.values(headers).map(value => this.escapeCsvValue(value)).join(",") + "\n";
    usersToExport.forEach(user => {
      const row = Object.keys(headers)
        .map(header => {
          if (header === "lastLoginDate") {
            const u = user as any;
            return this.escapeCsvValue(u.lastLoginDate || u.lastLoginAt || u.lastLogin || "");
          }

          return this.escapeCsvValue(user[header]);
        })
        .join(",");
      csv += row + "\n";
    });

    // download file
    let blob = new Blob([csv], { type: "text/csv" });
    let url = window.URL.createObjectURL(blob);
    let a = document.createElement("a");
    a.href = url;
    a.download = "Users.csv";
    document.body.appendChild(a);
    a.click();

    this.isExporting = false;
  }

  private escapeCsvValue(value: any): string {
    if (value === null || value === undefined) {
      return "N/A";
    }

    const stringValue = String(value);
    return /[",\n]/.test(stringValue)
      ? `"${stringValue.replace(/"/g, '""')}"`
      : stringValue;
  }

  addUser() {
    this.openManageUserDialog(null);
  }

  removeUser(user: IUser) {
    this.openManageUserDialog({
      id: user.id,
      email: user.email,
      lastName: user.lastName,
      firstName: user.firstName
    });
  }

  private openManageUserDialog(user: ManageUserDialogData | null) {
    this.dialog.open(ManageUserComponent, {
      width: '520px',
      data: user,
      autoFocus: 'input',
    });
  }

}


